import { Component, OnDestroy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { EstimateService } from 'src/app/estimate.service';
import { Phases, Phase, summary, PhaseMilestone } from '../../output-interfaces/output-interfaces';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { mockJsonInput } from "../../data/mock.constant";
import { Observable, Subscription } from 'rxjs';
import { DialogueComponent } from 'src/app/output/estimate-output/estimate-dialogue/estimate-dialogue.component';
import { MatDialog } from '@angular/material/dialog';
import { AiHubLoaderService } from '@aihub/ui/loader';


@Component({
  selector: 'app-estimate-table',
  templateUrl: './estimate-table.component.html',
  styleUrls: ['./estimate-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ])
  ]
})
export class EstimateTableComponent implements OnDestroy {
  public dataSource: any;
  public expandedId: string = 'not expanded';

  public isLoading$ = this.loaderService.loader$.asObservable();
  public disableGantChart$: Observable<boolean> = this.progress.disableGantChart.asObservable();
  public displayedColumns: string[] = ['Task', 'Time', 'Risk', 'Auto-Edit'];
  private prepareTableSub: Subscription;
  private initTableSub: Subscription;

  constructor(
    private progress: EstimateService,
    public dialog: MatDialog,
    private loaderService: AiHubLoaderService
  ) {
    this.initTableSub = this.progress.initTable.subscribe(() => {
      this.rebuildTable();
      this.updateTable();
    })
    this.prepareTableSub = this.progress.prepareOutputTable.subscribe((phase: Phase | undefined) => {
      if (phase) {
        this.prepareOutputTable(phase)
        this.progress.prepareOutputTable.next(undefined)
      }
      this.updateTable();
    })
    // Mock
    // this.progress.dataSource = new MatTableDataSource<Phases>();
    // mockJsonInput.phases.forEach(phaseJSON => {
    //   this.prepareOutputTable(phaseJSON);
    // });
    // this.dataSource = this.progress.dataSource;
  }

  rebuildTable(): void {
    this.progress.dataSource = new MatTableDataSource<Phases>();
    this.progress.displayedPhaseArray.phases.forEach((phase) => {
      setTimeout(() => {
        this.prepareOutputTable(phase);
      }, 10);
    })
  }

  prepareOutputTable(phaseJSON: Phase): void {
    let data: { phase: summary }[] = [];
    const phaseTime = this.calculatePhaseTime(phaseJSON);

    data = this.addPhaseData(phaseJSON, phaseTime);
    data = this.addMilestonesData(phaseJSON, data);

    if (!this.progress.dataSource.data.includes(data)) {
      this.progress.dataSource.data = [...this.progress.dataSource.data, ...data].sort(this.compareById);
    }
  }

  private addPhaseData(phaseJSON, phaseTime): { phase: summary }[] {
    this.progress.extractTimeUnit(phaseJSON.phaseMilestones[0].milestoneTime, true);
    const formattedTime = this.progress.formatTime(phaseTime);
    const phaseSummary: summary = {
      name: phaseJSON.phaseTitle,
      id: phaseJSON.phaseId,
      risk: phaseJSON.phaseRisk.riskLevel,
      level: phaseJSON.phaseRisk.riskDescription,
      timeOriginal: formattedTime,
      timeInteger: phaseTime,
      timeUnit: this.progress.extractTime(formattedTime).unit,
      details: phaseJSON.phaseDetails
    };
    return [{ phase: phaseSummary }];
  }

  private addMilestonesData(phaseJSON, data: { phase: summary }[]): { phase: summary }[] {
    phaseJSON.phaseMilestones.forEach((milestone: PhaseMilestone) => {
      const milestoneTime = this.progress.extractTime(milestone.milestoneTime).integer;
      const milestoneSummary: summary = {
        name: milestone.milestoneTitle,
        id: milestone.milestoneId,
        timeOriginal: this.progress.formatTime(milestoneTime),
        timeInteger: milestoneTime,
        timeUnit: this.progress.extractTime(milestone.milestoneTime).unit,
        details: milestone.milestoneDetails
      };
      data.push({ phase: milestoneSummary });
    });
    return data;
  }

  // Auto edit functions
  regenerate(id: string, instruction = ''): void {
    const currentPhase = JSON.parse(JSON.stringify(this.progress.getPhaseById(id)));
    this.removeFromTableById(id);
    this.removePhaseById(id);
    this.progress.replacePrompt(id, currentPhase, instruction);
    this.updateTable();
  }

  add(id: string): void {
    this.incrementIds(id, 'increase');
    this.rebuildTable();
    this.progress.addPrompt(id);
    this.updateTable();
  }

  remove(id: string): void {
    this.removePhaseById(id);
    this.incrementIds(id, 'decrease');
    this.rebuildTable();
    this.progress.dependencyPrompt();
    this.updateTable();
  }

  instruct(id: string): void {
    this.dialog.open(DialogueComponent);
    const dialogueSub = this.progress.dialogueInput.subscribe((instruction: string) => {
      this.regenerate(id, instruction);
      dialogueSub.unsubscribe();
      this.dialog.closeAll();
    })
  }

  // Helper functions
  updateTable() {
    this.dataSource = this.progress.dataSource;
    if (this.dataSource) this.dataSource._updateChangeSubscription();
  }

  toggleExpand(id: any) {
    if (this.expandedId === id) {
      this.expandedId = null;
    } else {
      this.expandedId = id;
    }
  }

  removeFromTableById(id: string): void {
    this.progress.dataSource.data = this.progress.dataSource.data.filter(row => row.phase.id !== id);
    this.progress.dataSource.data = this.progress.dataSource.data.filter(row => !row.phase.id.startsWith(id + '.'));
  }

  removePhaseById(id: string): void {
    this.progress.displayedPhaseArray.phases = this.progress.displayedPhaseArray.phases.filter(phase => phase.phaseId !== id);
  }

  incrementIds(id: string, direction: string): void {
    let updatedPhasesObj: Phases = JSON.parse(JSON.stringify(this.progress.displayedPhaseArray)); // deep copy of object

    updatedPhasesObj.phases.forEach((phase: Phase) => {
      let phaseId = parseInt(phase.phaseId);
      if (phaseId > Number(id)) {
        if (direction === 'increase') {
          phaseId++;
        } else {
          phaseId--;
        }
        phase.phaseId = phaseId.toString();

        phase.phaseMilestones.forEach((milestone: PhaseMilestone) => {
          let milestoneIdParts = milestone.milestoneId.split('.');
          let phaseIdPart = parseInt(milestoneIdParts[0]);

          if (phaseIdPart > Number(id)) {
            if (direction === 'increase') {
              phaseIdPart++;
            } else {
              phaseIdPart--;
            }
            milestone.milestoneId = `${phaseIdPart}.${milestoneIdParts[1]}`;
          }
        });
      }
    });

    this.progress.displayedPhaseArray = updatedPhasesObj;
  }

  compareById(a, b) {
    const idA = parseFloat(a.phase.id);
    const idB = parseFloat(b.phase.id);
    return idA - idB;
  }

  calculatePhaseTime(phase: Phase): number {
    return phase.phaseMilestones.reduce((phaseTime, milestone) => {
      const timeInteger = this.progress.extractTime(milestone.milestoneTime).integer;
      return phaseTime + timeInteger;
    }, 0);
  }

  ngOnDestroy(): void {
    this.initTableSub.unsubscribe();
    this.prepareTableSub.unsubscribe();
  }
}
